// How an invoice reads in lists (sculpt-api PLAN-payments.md M2): its number (none until Qonto numbers it), invoice or
// credit note, where the invoicing worker stands, and its amounts excluding VAT, VAT and including VAT.
import { useTranslation } from 'react-i18next';
import { money } from '../services/format';
import { orderVatLabel } from './vat';
import { Badge } from './ui';
import type { Invoice, InvoiceAdmin } from '../types/api';

type T = (key: string, options?: Record<string, unknown>) => string;

/** The number, or « en cours d'émission » while Qonto has not numbered it. */
export function invoiceNumber(invoice: Pick<Invoice, 'number'>, t: T): string {
  return invoice.number ?? t('invoices.issuing');
}

export function KindBadge({ invoice }: { invoice: Pick<Invoice, 'kind'> }) {
  const { t } = useTranslation();
  return <Badge tone={invoice.kind === 'credit_note' ? 'warn' : 'neutral'}>{t(`invoices.kind.${invoice.kind}`)}</Badge>;
}

/** Qonto or manual, and for a Qonto invoice where the worker stands: in progress (step, attempts), issued, failed. */
export function IssueBadge({ invoice }: { invoice: Pick<InvoiceAdmin, 'provider' | 'issue_state' | 'issue_step' | 'attempts'> }) {
  const { t } = useTranslation();
  if (invoice.provider !== 'qonto') return <Badge>{t('invoices.provider.manual')}</Badge>;
  const step = invoice.issue_step ? t(`invoices.step.${invoice.issue_step}`) : '';
  switch (invoice.issue_state) {
    case 'pending':
      return <Badge tone="accent">{invoice.attempts > 0 ? t('invoices.issue.pendingRetry', { step, attempts: invoice.attempts }) : t('invoices.issue.pending', { step })}</Badge>;
    case 'failed':
      return <Badge tone="bad">{t('invoices.issue.failed', { step })}</Badge>;
    default:
      return <Badge tone="good">{t('invoices.provider.qonto')}</Badge>;
  }
}

/** The VAT line of an invoice: its rate or what replaces one; a manual invoice of before carries none. */
export function invoiceVat(invoice: Pick<Invoice, 'vat_scheme' | 'vat_rate' | 'vat_amount' | 'currency'>, t: T, locale: string): string {
  if (!invoice.vat_scheme) return '—';
  const label = orderVatLabel(invoice, t, locale);
  return invoice.vat_scheme === 'standard' ? `${label} · ${money(invoice.vat_amount, invoice.currency, locale)}` : label;
}

/** Cells: excluding VAT, VAT, including VAT. */
export function InvoiceAmounts({ invoice }: { invoice: Invoice }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  return (
    <>
      <td className="px-3 py-2 align-middle font-mono text-[11.5px]">{money(invoice.amount, invoice.currency, l)}</td>
      <td className="px-3 py-2 align-middle font-mono text-[11.5px]">{invoiceVat(invoice, t, l)}</td>
      <td className="px-3 py-2 align-middle font-mono text-[11.5px]">{money(invoice.total, invoice.currency, l)}</td>
    </>
  );
}
